import jwt from "jwt-decode";
import Auth from "@aws-amplify/auth";
import {DateTime} from "luxon";

const LOGGED_IN_TOKEN_SESSION_STORE_KEY_NAME = 'idToken';

const COGNITO_GROUPS = 'cognito:groups';

export const ROLE_NAME = {
    CLINICIAN: 'clinicians',
    SUPER_ADMIN: 'super_admin',
    AUDITOR_READ_ONLY: 'auditor_read_only',
    STAFF: 'staff',
    OPERATION_SUPPORT: 'operation_support',
}

export const ROLES = [ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.CLINICIAN, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT];

export const CLINICIAN_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
}

export const getLoggedInPersonName = async () => {
    const token = await getJwtDecoded();
    const firstName = token && token['given_name']?.length > 0 ? token['given_name'] : 'UNKNOWN';
    const lastName = token && token['family_name']?.length > 0 ? token['family_name'] : 'UNKNOWN';
    return firstName + ' ' + lastName;
}

export const getUserRoles = async () => {
    const token = await getJwtDecoded();
    return token && token[COGNITO_GROUPS] ? token[COGNITO_GROUPS] : [];
}

//` 5 Types of Roles for the application
//@ 1. CLINICIAN - Can view and edit patient data
export const isClinicianRole = async () => {
    const token = await getJwtDecoded();
    return token && token[COGNITO_GROUPS].includes(ROLE_NAME.CLINICIAN);
}

//@ 2. SUPER_ADMIN - Can view and edit all data
export const isSuperAdminRole = async () => {
    const token = await getJwtDecoded();
    return token && token[COGNITO_GROUPS].includes(ROLE_NAME.SUPER_ADMIN);
}

//@ 3. AUDITOR_READ_ONLY - Can view all data but cannot edit
export const isAuditorReadOnlyRole = async () => {
    const token = await getJwtDecoded();
    return token && token[COGNITO_GROUPS].includes(ROLE_NAME.AUDITOR_READ_ONLY);
}

//@ 4. STAFF - Can view and edit patient data
export const hasStaffRole = async () => {
    const token = await getJwtDecoded();
    return token && token[COGNITO_GROUPS].includes(ROLE_NAME.STAFF);
}

//@ 5. OPERATION_SUPPORT - Can view and edit patient data
export const isOperationSupportRole = async () => {
    const token = await getJwtDecoded();
    return token && token[COGNITO_GROUPS].includes(ROLE_NAME.OPERATION_SUPPORT);
}

//` Some of the common access control checks
//@ 1. Users who have access to Product and Order data
export const hasAccessToProductAndOrderData = async () => {
    return await hasStaffRole() || await isClinicianRole() || await isSuperAdminRole();
}

//@ 2. Users who have access to Clinical data
export const hasAccessToClinicalData = async () => {
    const hasAccess = await (async () => {
        const [isSuperAdmin, isClinician] = await Promise.all([isSuperAdminRole(), isClinicianRole()]);
        return isSuperAdmin || isClinician;
    })();
    return hasAccess;
}

export const clinicianIdFromRefreshAuth = async () => {
    const decoded = await getJwtDecoded();
    return decoded?.sub || null;
};

export const getJwtDecoded = async () => {
    const rawJwt = await getRawJwt();
    return jwt(rawJwt);
};

export const getRawJwt = async () => {
    const token = await getLoggedInToken();
    return token?.jwt;
};

export const getLoggedInToken = async () => {
    const idToken = fetchParsedIdToken();
    if(isValidJwt(idToken?.jwt, idToken?.exp)) return idToken;
    return await (async () => {
        const token =  await Auth.currentSession().then(sess => {
            const renewedIdToken = {
                jwt: sess.getIdToken().getJwtToken(),
                exp: sess.getIdToken().getExpiration(),
                issuedAt: sess.getIdToken().getIssuedAt()};
            localStorage.setItem(LOGGED_IN_TOKEN_SESSION_STORE_KEY_NAME, JSON.stringify(renewedIdToken));
            return renewedIdToken;
        }).catch(err => {
            console.error(err);
            clearIdToken();
            return null;
        });
        return token;
    })();
};

export const clearIdToken = () => {
    localStorage.removeItem(LOGGED_IN_TOKEN_SESSION_STORE_KEY_NAME);
};

const fetchParsedIdToken = () => {
    const tokenStr = localStorage.getItem(LOGGED_IN_TOKEN_SESSION_STORE_KEY_NAME);
    return tokenStr ? JSON.parse(tokenStr) : {};
};

const isValidJwt = (jwt, exp) => {
    //make sure token has atleast 2 mins to expire
    return jwt && exp && (exp - DateTime.now().toSeconds()) > 120;
}
